import { useState, useRef, useEffect } from 'react'

/**
 * AppSelect — unified dropdown for the entire app
 *
 * Usage:
 * <AppSelect
 *   value={selected}
 *   onChange={setSelected}
 *   options={[{ value: 'a', label: 'Option A' }]}
 *   placeholder="Choose one..."
 * />
 */
export default function AppSelect({
  value,
  onChange,
  options = [],
  placeholder = 'Select...',
  disabled = false,
  className = '',
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  const selected = options.find(o => o.value === value)

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    const handleKey = (e) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKey)
    }
  }, [])

  return (
    <div
      className={`app-select ${open ? 'app-select--open' : ''} ${disabled ? 'app-select--disabled' : ''} ${className}`}
      ref={ref}
    >
      <button
        className="app-select__trigger"
        onClick={() => !disabled && setOpen(o => !o)}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
      >
        <span className={`app-select__value ${!selected ? 'app-select__placeholder' : ''}`}>
          {selected ? selected.label : placeholder}
        </span>
        <span className="app-select__chevron">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="app-select__dropdown" role="listbox">
          {options.map(option => (
            <button
              key={option.value}
              className={`app-select__option ${option.value === value ? 'app-select__option--selected' : ''}`}
              onClick={() => { onChange(option.value); setOpen(false) }}
              role="option"
              aria-selected={option.value === value}
              type="button"
            >
              {option.icon && <span className="app-select__option-icon">{option.icon}</span>}
              {option.label}
              {option.value === value && <span className="app-select__option-check">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
